import type { FC, ReactElement, FormEvent } from 'react';
import { useState, useEffect, useCallback } from 'react';
import './TimeIn.css';
import LocationMap from './components/LocationMap';
import LocationForm from './components/LocationForm';
import TimeInHistory from './components/TimeInHistory';
import PermissionRequest from './components/PermissionRequest';
import Modal from './components/Modal';
import { loadGoogleMapsScript } from './utils/maps';
import { supabase } from '../../lib/supabaseClient';
import { useStudentAuth } from '../../lib/AuthProvider';


/// <reference types="@types/google.maps" />

// Declare google maps to avoid TS errors
declare global {
  interface Window {
    google: {
      maps: typeof google.maps;
    };
    initMap?: () => void;
  }
}

// Type definitions
export interface Coordinates {
  lat: number;
  lng: number;
}

export interface TimeHistoryEntry {
  type: 'in' | 'out';
  time: Date;
  location: string;
  coordinates: Coordinates;
}

export interface SavedLocation {
  id: string;
  name: string;
  radius: number;
  lat: number;
  lng: number;
  is_active: boolean;
  approval_status: 'pending' | 'approved' | 'rejected';
}

export interface LocationState {
  current: Coordinates | null;
  lastAccurate: Coordinates | null;
  selected: Coordinates | null;
  saved: SavedLocation | null;
  savedLocations: SavedLocation[];
}

interface TimeInProps {
  onClose: () => void;
}

const TimeIn: FC<TimeInProps> = ({ onClose }): ReactElement | null => {
  // Get loading state from AuthProvider
  const { loading: authLoading } = useStudentAuth();
  
  // Show loading state while auth is loading
  if (authLoading) {
    return (
      <div className="timein-modal-overlay">
        <div className="timein-modal">
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <div>Loading your profile...</div>
            <div style={{ fontSize: '0.8rem', marginTop: '10px', color: '#666' }}>
              Please wait a moment...
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Constants
  const MIN_ACCURACY = 50; // meters - only accept locations with accuracy better than this
  const SIGNIFICANT_CHANGE = 5; // meters - minimum distance to consider as actual movement

  // Consolidated location state
  const [locationState, setLocationState] = useState<LocationState>({
    current: null,
    lastAccurate: null,
    selected: null,
    saved: null,
    savedLocations: []
  });
  const [isWithinRadius, setIsWithinRadius] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<'prompt' | 'granted' | 'denied'>('prompt');
  const [showAddLocation, setShowAddLocation] = useState(false);

  // UI state
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [radius, setRadius] = useState(5); // Default radius in meters
  const [status, setStatus] = useState('Not in designated area');
  const [lastAction, setLastAction] = useState('None');

  // Time in/out state (isTimedIn derived from hasAmTimeIn/hasPmTimeIn)
  const [timeInTime, setTimeInTime] = useState<Date | null>(null);
  const [timeInHistory, setTimeInHistory] = useState<TimeHistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  
  // Time restriction state
  const [hasAmTimeIn, setHasAmTimeIn] = useState(false);
  const [hasAmTimeOut, setHasAmTimeOut] = useState(false);
  const [hasPmTimeIn, setHasPmTimeIn] = useState(false);
  const [hasPmTimeOut, setHasPmTimeOut] = useState(false);

  // Helper functions for time restrictions
  const isInAmWindow = (): boolean => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    // AM: 8:00 AM to 12:58 PM
    if (hours === 7 && minutes >= 0) return true;
    if (hours >= 9 && hours < 12) return true;
    if (hours === 12 && minutes <= 58) return true;
    return false;
  };

  const isInPmWindow = (): boolean => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    // PM: 12:59 PM to 5:59 PM
    if (hours === 12 && minutes >= 59) return true;
    if (hours >= 13 && hours < 17) return true;
    if (hours === 17 && minutes <= 59) return true;
    return false;
  };

  // Check existing time ins for today (preserving database field names)
  const checkTodayTimeIns = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;

      const today = new Date().toISOString().split('T')[0];
      
      const { data: todayCheckIns, error } = await supabase
        .from('check_ins')
        .select('check_type')
        .eq('user_id', authUser.id)
        .eq('check_date', today);

      if (error) {
        console.error('Error checking today time ins:', error);
        return;
      }

      if (todayCheckIns) {
        const checkIns = todayCheckIns as Array<{ check_type: string }>;
        setHasAmTimeIn(checkIns.some(ci => ci.check_type === 'check_in_am'));
        setHasAmTimeOut(checkIns.some(ci => ci.check_type === 'check_out_am'));
        setHasPmTimeIn(checkIns.some(ci => ci.check_type === 'check_in_pm'));
        setHasPmTimeOut(checkIns.some(ci => ci.check_type === 'check_out_pm'));
      }
    } catch (error) {
      console.error('Error checking today time ins:', error);
    }
  };

  // Load time in history from database using typed supabase client
  useEffect(() => {
    const loadTimeInHistory = async () => {
      setIsLoadingHistory(true);
      setHistoryError(null);
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        
        if (!authUser) {
          setHistoryError('Please log in to view time in history');
          return;
        }

        // Load recent check-ins and join to locations to get the location name (preserving database table name)
        const { data: checkIns, error } = await supabase
          .from('check_ins')
          .select('check_type, check_time, coordinates, location:locations(name)')
          .eq('user_id', authUser.id)
          .order('check_time', { ascending: false })
          .limit(50) as { data: any[] | null, error: any };

        if (error) {
          return;
        }

        if (checkIns) {
          const history: TimeHistoryEntry[] = checkIns.map(checkIn => ({
            type: checkIn.check_type.includes('in') ? 'in' : 'out',
            time: new Date(checkIn.check_time),
            location: (checkIn.location && checkIn.location.name) ? checkIn.location.name : 'Unknown location',
            coordinates: checkIn.coordinates as unknown as Coordinates
          }));
          setTimeInHistory(history);
        }
      } catch (error) {
        console.error('Error loading time in history:', error);
        setHistoryError('Failed to load time in history');
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadTimeInHistory();
    checkTodayTimeIns();
    
    // Refresh time in status every minute
    const interval = setInterval(() => {
      checkTodayTimeIns();
    }, 60000);

    return () => clearInterval(interval);
  }, []); // Load history on component mount

  const getCurrentLocation = useCallback((): void => {
    if (!navigator.geolocation) {
      setStatus('Geolocation is not supported by your browser');
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setLocationState(prev => ({ ...prev, current: newLocation }));
        setStatus('Location acquired successfully');
      },
      (error) => {
        switch (error.code) {
          case GeolocationPositionError.PERMISSION_DENIED:
            setStatus('Location access was denied');
            setPermissionStatus('denied');
            break;
          case GeolocationPositionError.POSITION_UNAVAILABLE:
            setStatus('Unable to determine your location');
            break;
          case GeolocationPositionError.TIMEOUT:
            setStatus('Location request timed out');
            break;
          default:
            setStatus('Error getting location');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }, []);



  const calculateDistance = useCallback((coords1: Coordinates, coords2: Coordinates): number => {
    const R = 6371e3; // Earth's radius in meters
    const lat1Rad = (coords1.lat * Math.PI) / 180;
    const lat2Rad = (coords2.lat * Math.PI) / 180;
    const deltaLat = ((coords2.lat - coords1.lat) * Math.PI) / 180;
    const deltaLng = ((coords2.lng - coords1.lng) * Math.PI) / 180;

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1Rad) * Math.cos(lat2Rad) *
      Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  // Consolidated distance checking function
  const checkAndUpdateDistance = useCallback((coords: Coordinates, accuracy?: number) => {
    if (locationState.saved) {
      const distance = calculateDistance(coords, {
        lat: locationState.saved.lat,
        lng: locationState.saved.lng
      });
      const isInRange = distance <= locationState.saved.radius;
      setIsWithinRadius(isInRange);
      const accuracyText = accuracy !== undefined ? `, Accuracy: ${accuracy.toFixed(1)}m` : '';
      setStatus(isInRange ? 
        `Within designated area (${Math.round(distance)}m from location${accuracyText})` : 
        `Outside designated area (${Math.round(distance)}m from location${accuracyText})`
      );
    }
  }, [locationState.saved, calculateDistance]);


  // Load saved locations on component mount
  useEffect(() => {
    const loadSavedLocations = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: locations, error } = await supabase
          .from('locations')
          .select()
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error loading saved locations:', error);
          return;
        }

        if (locations) {
          const loadedLocations: SavedLocation[] = (locations as Array<{
            id: string;
            name: string;
            radius: number;
            latitude: number;
            longitude: number;
            is_active: boolean;
            approval_status: 'pending' | 'approved' | 'rejected';
          }>).map(location => ({
            id: location.id,
            name: location.name,
            radius: location.radius,
            lat: location.latitude,
            lng: location.longitude,
            is_active: location.is_active,
            approval_status: location.approval_status || 'pending'
          }));
          setLocationState(prev => ({ ...prev, savedLocations: loadedLocations }));
          // Set the active location if there is one
          const activeLocation = loadedLocations.find(loc => loc.is_active);
          if (activeLocation) {
            setLocationState(prev => ({ 
              ...prev, 
              saved: activeLocation,
              selected: { lat: activeLocation.lat, lng: activeLocation.lng }
            }));
            setLocationName(activeLocation.name);
            setRadius(activeLocation.radius);
          } else if (loadedLocations.length > 0) {
            // Set the first location as active if none are marked as active
            const firstLocation = loadedLocations[0];
            setLocationState(prev => ({ 
              ...prev, 
              saved: firstLocation,
              selected: { lat: firstLocation.lat, lng: firstLocation.lng }
            }));
            setLocationName(firstLocation.name);
            setRadius(firstLocation.radius);
          }
        } else {
          setLocationState(prev => ({ ...prev, savedLocations: [] }));
        }
      } catch (error) {
        console.error('Error loading saved locations:', error);
        setLocationState(prev => ({ ...prev, savedLocations: [] }));
      }
    };

    loadSavedLocations();
  }, []); // Load locations on component mount


  const startLocationTracking = useCallback((): (() => void) => {
    let watchId: number;
    let retryTimeout: NodeJS.Timeout;
    let retryCount = 0;
    const MAX_RETRIES = 3;

    const setupWatcher = () => {
      try {
        watchId = navigator.geolocation.watchPosition(
          (position) => {
            // Reset retry count on successful update
            retryCount = 0;
            
            const newCoords: Coordinates = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };

            // Always set current location immediately so blue marker appears right away
            setLocationState(prev => {
              const isFirstLocation = prev.current === null;
              const updatedState = { ...prev, current: newCoords };
              
              // Only update lastAccurate if accuracy is good enough (≤50m) for distance calculations
              if (position.coords.accuracy <= MIN_ACCURACY) {
                // Check if this is a significant change from last accurate location
                if (!prev.lastAccurate || calculateDistance(prev.lastAccurate, newCoords) >= SIGNIFICANT_CHANGE) {
                  updatedState.lastAccurate = newCoords;
                  checkAndUpdateDistance(newCoords, position.coords.accuracy);
                  setStatus(`Location updated (Accuracy: ${position.coords.accuracy.toFixed(1)}m)`);
                } else {
                  // Still update current location even if not significant change
                  setStatus(`Location: ${position.coords.accuracy.toFixed(1)}m accuracy`);
                }
              } else {
                // Location with lower accuracy - still show it but note the accuracy
                if (isFirstLocation) {
                  setStatus(`Location acquired (Accuracy: ${position.coords.accuracy.toFixed(1)}m, improving...)`);
                } else {
                  setStatus(`Location: ${position.coords.accuracy.toFixed(1)}m accuracy (waiting for better)`);
                }
                // Still check distance if we have a saved location
                if (prev.saved) {
                  checkAndUpdateDistance(newCoords);
                }
              }
              
              return updatedState;
            });
            
            setPermissionStatus('granted');
          },
          (error: GeolocationPositionError) => {
            if (error.code === GeolocationPositionError.TIMEOUT) {
              if (retryCount < MAX_RETRIES) {
                retryCount++;
                setStatus(`Location update timed out. Retrying... (Attempt ${retryCount}/${MAX_RETRIES})`);
                
                // Clear existing watcher and retry after a short delay
                if (watchId) {
                  navigator.geolocation.clearWatch(watchId);
                }
                retryTimeout = setTimeout(setupWatcher, 2000);
                return;
              }
            }

            switch (error.code) {
              case GeolocationPositionError.PERMISSION_DENIED:
                setStatus('Location access was denied');
                setPermissionStatus('denied');
                break;
              case GeolocationPositionError.POSITION_UNAVAILABLE:
                setStatus('Unable to determine your location. Please check your device settings.');
                getCurrentLocation(); // Try to get a single position update
                break;
              case GeolocationPositionError.TIMEOUT:
                if (retryCount >= MAX_RETRIES) {
                  setStatus('Location tracking failed. Please refresh the page or check your device settings.');
                }
                break;
              default:
                setStatus('Error getting location. Please try again.');
            }
          },
          {
            enableHighAccuracy: true,
            maximumAge: 2000,     // Allow cached positions up to 2 seconds old
            timeout: 10000        // 10 seconds timeout
          }
        );
      } catch (error) {
        console.error('Error setting up location watcher:', error);
        setStatus('Error setting up location tracking');
      }
    };

    try {
      // Start the initial watcher
      setupWatcher();

      // Return cleanup function
      return () => {
        if (watchId) {
          navigator.geolocation.clearWatch(watchId);
        }
        if (retryTimeout) {
          clearTimeout(retryTimeout);
        }
      };
    } catch (error) {
      setStatus('Error setting up location tracking');
      return () => {}; // Return empty cleanup function on error
    }
}, [locationState.saved, locationState.lastAccurate, calculateDistance, checkAndUpdateDistance, getCurrentLocation]);

  const requestLocationPermission = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setPermissionStatus('granted');
        setShowModal(true);
        const coords: Coordinates = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setLocationState(prev => ({ ...prev, current: coords }));
        startLocationTracking();
        
        // Check if location is already saved
        if (locationState.saved) {
          checkAndUpdateDistance(coords);
        }
      },
      (error) => {
        // Check if it's a timeout error
        if (error.code === error.TIMEOUT) {
          setStatus('Location request timed out. Please try again.');
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          setStatus('Unable to determine your location. Please check your device settings.');
        } else {
          setPermissionStatus('denied');
          setStatus('Location permission denied');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }, [locationState.saved, startLocationTracking, checkAndUpdateDistance]);

  // Consolidated location permission and tracking setup
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let permResult: PermissionStatus | undefined;
    
    const handlePermissionChange = () => {
      if (!permResult) return;
      setPermissionStatus(permResult.state as 'prompt' | 'granted' | 'denied');
      if (permResult.state === 'granted') {
        if (cleanup) cleanup();
        cleanup = startLocationTracking();
        setShowModal(true);
      }
    };

    const checkAndRequestLocation = async () => {
      if (!navigator.geolocation) {
        setStatus('Geolocation is not supported by your browser');
        return;
      }

      try {
        // Check if geolocation permission is already granted
        if (navigator.permissions) {
          permResult = await navigator.permissions.query({ name: 'geolocation' });
          setPermissionStatus(permResult.state as 'prompt' | 'granted' | 'denied');

          if (permResult.state === 'granted') {
            // If already granted, start tracking immediately
            cleanup = startLocationTracking();
            setShowModal(true);
            
            // Listen for permission changes
            permResult.addEventListener('change', handlePermissionChange);
          } else if (permResult.state === 'prompt') {
            // If not granted, request permission
            requestLocationPermission();
          }
        } else {
          // Fall back to direct geolocation request if permissions API not available
          requestLocationPermission();
        }
      } catch (error) {
        console.error('Error checking location permission:', error);
        // Fall back to direct geolocation request
        requestLocationPermission();
      }
    };

    checkAndRequestLocation();

    // Cleanup function
    return () => {
      if (cleanup) {
        cleanup();
      }
      if (permResult) {
        permResult.removeEventListener('change', handlePermissionChange);
      }
    };
  }, [startLocationTracking, requestLocationPermission]);

  // Load Google Maps script and set loading to false once script loads
  useEffect(() => {
    loadGoogleMapsScript(() => {
      // Set loading to false once Google Maps script is ready
      // Map can render even without location initially
      setIsMapLoading(false);
    });
  }, []);

  // Check distance when saved location changes
  useEffect(() => {
    if (locationState.current && locationState.saved) {
      checkAndUpdateDistance(locationState.current);
    }
  }, [locationState.saved, locationState.current, checkAndUpdateDistance]);





  const handleTimeAction = async (action: 'in' | 'out'): Promise<void> => {
    if (!isWithinRadius) {
      alert('You must be within the designated area to time ' + action);
      return;
    }
    
    if (!locationState.current || !locationState.saved) {
      alert('Current location or saved work location not available');
      return;
    }

    const now = new Date();
    const isAm = isInAmWindow();
    const isPm = isInPmWindow();
    
    // TEMPORARILY DISABLED: Check time restrictions
    // if (action === 'in') {
    //   if (isAm) {
    //     if (hasAmTimeIn) {
    //       alert('You have already timed in for the AM. Please wait for PM (12:59 PM - 5:59 PM)');
    //       return;
    //     }
    //   } else if (isPm) {
    //     if (hasPmTimeIn) {
    //       alert('You have already timed in for the PM');
    //       return;
    //     }
    //     // Must have completed AM time out if AM session was started
    //     if (hasAmTimeIn && !hasAmTimeOut) {
    //       alert('You must complete your AM time out before timing in for PM');
    //       return;
    //     }
    //   } else {
    //     alert('Time In is only allowed during AM (8:00 AM - 12:58 PM) or PM (12:59 PM - 5:59 PM)');
    //     return;
    //   }
    // } else {
    //   // Time out action
    //   if (isAm) {
    //     if (!hasAmTimeIn) {
    //       alert('You must time in for AM before timing out');
    //       return;
    //     }
    //     if (hasAmTimeOut) {
    //       alert('You have already timed out for the AM');
    //       return;
    //     }
    //   } else if (isPm) {
    //     if (!hasPmTimeIn) {
    //       alert('You must time in for PM before timing out');
    //       return;
    //     }
    //     if (hasPmTimeOut) {
    //       alert('You have already timed out for the PM');
    //       return;
    //     }
    //   } else {
    //     alert('Time Out is only allowed during AM (8:00 AM - 12:58 PM) or PM (12:59 PM - 5:59 PM)');
    //     return;
    //   }
    // }

    const historyEntry = {
      type: action,
      time: now,
      location: locationState.saved.name,
      coordinates: locationState.current
    };

    // Determine session type: use isAm/isPm for labeling, default to PM if outside windows
    const sessionType = isAm ? 'AM' : (isPm ? 'PM' : 'PM');
    if (action === 'in') {
      setTimeInTime(now);
      setLastAction(`Timed in at ${now.toLocaleTimeString()} (${sessionType})`);
      setStatus(`Timed in at ${locationState.saved.name}`);
      
      // Update state based on AM/PM
      if (isAm) {
        setHasAmTimeIn(true);
      } else {
        setHasPmTimeIn(true);
      }
    } else {
      const duration = timeInTime ? Math.round((now.getTime() - timeInTime.getTime()) / (1000 * 60)) : 0;
      setLastAction(`Timed out at ${now.toLocaleTimeString()} (Duration: ${duration} minutes, ${sessionType})`);
      setTimeInTime(null);
      setStatus('Timed out');
      
      // Update state based on AM/PM
      if (isAm) {
        setHasAmTimeOut(true);
      } else {
        setHasPmTimeOut(true);
      }
    }

    // Add to history
    setTimeInHistory(prev => [...prev, historyEntry]);

    // Save time in/out to the database using Database types (preserving database field names)
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        throw new Error('User not authenticated');
      }

      // Get user profile data
      const { data: profile, error: profileError } = await supabase
        .from('student_profile')
        .select('first_name, last_name, student_id')
        .eq('id', authUser.id)
        .single() as { data: { first_name: string; last_name: string; student_id: string } | null; error: any };

      if (profileError || !profile) {
        throw new Error(profileError?.message || 'Profile not found');
      }

      // Get location ID
      const { data: locationData, error: locationError } = await supabase
        .from('locations')
        .select('id')
        .eq('user_id', authUser.id)
        .eq('is_active', true)
        .single() as { data: { id: string } | null; error: any };

      if (locationError || !locationData) {
        throw new Error(locationError?.message || 'Active location not found');
      }

      // Determine check type based on time and action (preserving database enum values)
      const checkHours = now.getHours();
      const checkMinutes = now.getMinutes();
      const isCheckAm = (checkHours < 12) || (checkHours === 12 && checkMinutes <= 58);
      const checkType = action === 'in' 
        ? (isCheckAm ? 'check_in_am' : 'check_in_pm')
        : (isCheckAm ? 'check_out_am' : 'check_out_pm');

      // Create check-in data object
      const checkInData = {
        user_id: authUser.id,
        student_id: profile.student_id,
        first_name: profile.first_name,
        last_name: profile.last_name,
        location_id: locationData.id,
        check_type: checkType,
        check_time: now.toISOString(),
        check_date: now.toISOString().split('T')[0],
        is_within_radius: isWithinRadius,
        coordinates: locationState.current
      };

      const { error: insertError } = await (supabase
        .from('check_ins')
        .insert(checkInData as any) as any);

      if (insertError) {
        throw insertError;
      }

      // Refresh today's time in status after saving
      await checkTodayTimeIns();

    } catch (error) {
      console.error('Error saving time in/out:', error);
      alert('Error saving time in/out. Please try again.');
    }
  };

  const handleLocationSelect = (location: Coordinates): void => {
    setLocationState(prev => ({ ...prev, selected: location }));
  };

  const handleSelectActiveLocation = async (locationId: string): Promise<void> => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        alert('You must be logged in to select a location');
        return;
      }

      // Deactivate all locations
      await ((supabase.from('locations') as any)
        .update({ is_active: false })
        .eq('user_id', authUser.id));

      // Activate the selected location
      await ((supabase.from('locations') as any)
        .update({ is_active: true })
        .eq('id', locationId));

      // Update local state and form fields in one atomic operation
      setLocationState(prev => {
        const updatedLocations = prev.savedLocations.map(loc => ({
          ...loc,
          is_active: loc.id === locationId
        }));
        
        const selectedLoc = updatedLocations.find(loc => loc.id === locationId);
        
        if (selectedLoc) {
          // Update form fields immediately with the selected location data
          setLocationName(selectedLoc.name);
          setRadius(selectedLoc.radius);
          
          // Check distance if current location is available
          if (prev.current) {
            checkAndUpdateDistance(prev.current);
          }
          
          return {
            ...prev,
            savedLocations: updatedLocations,
            saved: selectedLoc,
            selected: { lat: selectedLoc.lat, lng: selectedLoc.lng }
          };
        }
        
        return {
          ...prev,
          savedLocations: updatedLocations
        };
      });
    } catch (error) {
      console.error('Error selecting location:', error);
      alert('Error selecting location');
    }
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!locationState.selected) {
      alert('Please select a work location on the map');
      return;
    }

    if (!locationName.trim()) {
      alert('Please enter a location name');
      return;
    }

    if (radius < 5 || radius > 20) {
      alert('Please enter a radius between 5 and 20 meters');
      return;
    }

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        alert('You must be logged in to save a location');
        return;
      }

      // Create a properly typed location object using the Database types
      const locationData = {
        user_id: authUser.id,
        name: locationName.trim(),
        latitude: locationState.selected.lat,
        longitude: locationState.selected.lng,
        radius: radius,
        is_active: false, // New locations are not active by default
        approval_status: 'pending' // Requires OJT Coordinator approval
      };

      // Insert new location and get the inserted data
      const result = await (supabase
        .from('locations')
        .insert(locationData as any)
        .select()
        .single() as any);
      
      const insertedLocation = result?.data as {
        id: string;
        name: string;
        radius: number;
        latitude: number;
        longitude: number;
        is_active: boolean;
        approval_status: 'pending' | 'approved' | 'rejected';
      } | null;
      const insertError = result?.error;
        
      if (insertError || !insertedLocation) {
        throw insertError || new Error('Failed to insert location');
      }

      // Map the returned data to SavedLocation interface
      const savedLocation: SavedLocation = {
        id: insertedLocation.id,
        name: insertedLocation.name,
        radius: insertedLocation.radius,
        lat: insertedLocation.latitude,
        lng: insertedLocation.longitude,
        is_active: insertedLocation.is_active,
        approval_status: insertedLocation.approval_status
      };
      
      setLocationState(prev => ({
        ...prev,
        savedLocations: [...prev.savedLocations, savedLocation],
        saved: savedLocation
      }));
      
      // Ensure location tracking is active after saving
      if (permissionStatus === 'granted') {
        if (locationState.current) {
          checkAndUpdateDistance(locationState.current);
        }
        startLocationTracking();
        setStatus(`Location "${locationName}" saved with ${radius}m radius`);
      } else {
        // Re-request location permission if needed
        requestLocationPermission();
      }
    } catch (error: any) {
      console.error('Error saving location:', error);
      if (error?.code === '42P01' && error?.message?.includes('profiles')) {
        alert('Database schema error: The locations table references a profiles table that doesn\'t exist. The table has been renamed to student_profile. Please contact your administrator.');
      } else {
        alert(`Error saving location: ${error?.message || 'Unknown error'}`);
      }
    }
  };

  // requestLocationPermission is now defined above

  // Show permission request modal if permission is not granted
  if (!showModal) {
    return (
      <PermissionRequest
        permissionStatus={permissionStatus}
        status={status}
        onRequestPermission={requestLocationPermission}
        onClose={onClose}
      />
    );
  }


  return (
    <Modal
      isOpen={showModal}
      onClose={onClose}
      title="Time In / Time Out"
    >
      <LocationMap
        currentLocation={locationState.current}
        selectedLocation={locationState.selected}
        radius={radius}
        isLoading={isMapLoading}
        onLocationSelect={handleLocationSelect}
        onMarkerDrag={(location) => setLocationState(prev => ({ ...prev, selected: location }))}
      />

      <LocationForm
        locationName={locationName}
        radius={radius}
        isTimedIn={(hasAmTimeIn && !hasAmTimeOut) || (hasPmTimeIn && !hasPmTimeOut)}
        isWithinRadius={isWithinRadius}
        timeInTime={timeInTime}
        lastAction={lastAction}
        onLocationNameChange={setLocationName}
        onRadiusChange={setRadius}
        onTimeAction={handleTimeAction}
        onSubmit={handleSubmit}
        savedLocation={locationState.saved}
        savedLocations={locationState.savedLocations}
        showAddLocation={showAddLocation}
        onToggleAddLocation={() => setShowAddLocation(!showAddLocation)}
        onSelectLocation={handleSelectActiveLocation}
        hasAmTimeIn={hasAmTimeIn}
        hasAmTimeOut={hasAmTimeOut}
        hasPmTimeIn={hasPmTimeIn}
        hasPmTimeOut={hasPmTimeOut}
        isInAmWindow={isInAmWindow()}
        isInPmWindow={isInPmWindow()}
      />

      <TimeInHistory 
        history={timeInHistory}
        isLoading={isLoadingHistory}
        error={historyError}
      />
    </Modal>
  );
};



export default TimeIn;

